'use client'

import React, { useEffect, useState } from 'react'
import { X, Sparkles, Check } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { createClientComponentClient } from '@/integrations/supabase/client'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

interface SubscriptionPlan {
  id: string
  plan_key: string | null
  name: string
  description: string | null
  price_monthly: number | null
  price_yearly: number | null
  features: string[]
}

interface SubscriptionUpsellModalProps {
  isOpen: boolean
  onClose: () => void
  currentCount: number
  maxAllowed: number
}

export default function SubscriptionUpsellModal({ 
  isOpen, 
  onClose, 
  currentCount, 
  maxAllowed 
}: SubscriptionUpsellModalProps) {
  const [premiumPlan, setPremiumPlan] = useState<SubscriptionPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [upgrading, setUpgrading] = useState(false)
  const supabase = createClientComponentClient()

  useEffect(() => {
    const fetchPremiumPlan = async () => {
      try {
        const { data, error } = await supabase
          .from('subscription_plans')
          .select('*')
          .eq('is_active', true)
          .neq('plan_key', 'free')
          .order('price_monthly', { ascending: true })
          .limit(1)
          .single()

        if (error) throw error
        setPremiumPlan({
          ...data,
          features: Array.isArray(data.features) ? (data.features as string[]) : []
        })
      } catch (error) {
        console.error('Error fetching premium plan:', error)
      } finally {
        setLoading(false)
      }
    }

    if (isOpen) {
      fetchPremiumPlan()
    }
  }, [isOpen, supabase])

  const handleUpgrade = async () => {
    if (!premiumPlan?.plan_key) return
    
    setUpgrading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('No active session')

      const { data, error } = await supabase.functions.invoke('billing', {
        body: { 
          action: 'checkout', 
          plan: premiumPlan.plan_key, 
          interval: 'month' 
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      })

      if (error) throw error
      if (data?.url) window.location.href = data.url
    } catch (error) {
      console.error('Upgrade error:', error)
      alert('Unable to start upgrade. Please try again.')
    } finally {
      setUpgrading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200 animate-in zoom-in-95 duration-200">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close modal"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <div className="p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
            </div>

            {/* Title & Message */}
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-3 text-white">
              You've reached your conversation limit
            </h2>
            <p className="text-center text-gray-600 mb-2">
              You've used <span className="font-semibold text-gray-900">{currentCount} of {maxAllowed}</span> conversations this month.
            </p>
            <p className="text-center text-gray-600 mb-8">
              Upgrade to premium for unlimited conversations and more features!
            </p>

            {/* Premium Plan Card */}
            {premiumPlan && (
              <div className="bg-white rounded-xl border-2 border-emerald-200 p-6 mb-6 shadow-sm">
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-3xl font-bold text-gray-900">
                    ${premiumPlan.price_monthly}
                  </span>
                  <span className="text-gray-500">/month</span>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-4 text-white">
                  {premiumPlan.name} Plan
                </h3>

                <ul className="space-y-3">
                  {premiumPlan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleUpgrade}
                disabled={upgrading || !premiumPlan}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {upgrading ? (
                  <div className="flex items-center justify-center gap-2">
                    <LoadingSpinner size="sm" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  'Upgrade to Premium'
                )}
              </Button>
              <Button
                onClick={onClose}
                variant="outline"
                className="w-full py-3 rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-medium"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
